import { Grid2, type Grid2Props } from '@mui/material';
import { Form, Formik } from 'formik';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { boolean, object, string, type Schema } from 'yup';

import ContentLoader from '@components/ContentLoader';
import {
  FormikDropdownField,
  FormikTelField,
  FormikTextField,
  FormikToggleField,
} from '@components/FormikFields';
import RoundedButton from '@components/RoundedButton';

import { useUpdateAccount } from '@hooks/api/account';
import useCommonSchemas from '@hooks/schemas/useCommonSchemas';
import { useCountryRegion } from '@hooks/useCountryRegion';

import type { OrganizationFormValues } from '@models/account';

import { type GetOrganizationProfileQuery } from 'generated/account.generated';

const messages = defineMessages({
  organizationName: {
    id: 'organization.setting.profile.organizationName',
    defaultMessage: 'Organization Name',
  },
  sameBillingAddress: {
    id: 'organization.setting.profile.sameBillingAddress',
    defaultMessage: 'Organization address is same as billing address',
  },
  address: {
    id: 'GLOBAL.address',
    defaultMessage: 'Address',
  },
  address2: {
    id: 'GLOBAL.address2',
    defaultMessage: 'Address 2',
  },
  city: {
    id: 'GLOBAL.city',
    defaultMessage: 'City',
  },
  region: {
    id: 'GLOBAL.region',
    defaultMessage: 'State/Province',
  },
  country: {
    id: 'GLOBAL.country',
    defaultMessage: 'Country',
  },
  postalCode: {
    id: 'GLOBAL.postalCode',
    defaultMessage: 'Postal/Zip Code',
  },
  email: {
    id: 'GLOBAL.email',
    defaultMessage: 'Email Address',
  },
  phone: {
    id: 'GLOBAL.phone',
    defaultMessage: 'Phone Number',
  },
  required: {
    id: 'GLOBAL.required',
    defaultMessage: 'This field is required',
  },
  submit: {
    id: 'GLOBAL.submit',
    defaultMessage: 'Submit',
  },
});

interface OrgProfileFormProps {
  loading?: boolean;
  orgProfile?: GetOrganizationProfileQuery['getAccountRecord'];
}

export default function OrgProfileForm({
  loading: baseLoading,
  orgProfile,
}: OrgProfileFormProps) {
  const { formatMessage } = useIntl();
  const { emailSchema, phoneSchema } = useCommonSchemas();
  const { countryOptions, getProvinceOptions } = useCountryRegion();
  const [updateAccount, updateAccountLoading] = useUpdateAccount();

  const loading = baseLoading || updateAccountLoading;

  const initialValues: OrganizationFormValues = {
    org_name: orgProfile?.account_name ?? '',
    same_as_billing_address: false,
    address: orgProfile?.account_address ?? '',
    address2: orgProfile?.account_address2 ?? '',
    postal_code: orgProfile?.account_zip_postal ?? '',
    city: orgProfile?.account_city ?? '',
    region: orgProfile?.account_region_id ?? '',
    country: orgProfile?.account_country_id ?? '',
    email: orgProfile?.email ?? '',
    phone_number: orgProfile?.phone_number ?? '',
  };

  const handleSubmit = async (values: OrganizationFormValues) => {
    await updateAccount({
      account_name: values.org_name,
      email: values.email,
      phone_number: values.phone_number,
      account_address: values.address,
      account_address2: values.address2,
      account_city: values.city,
      account_region_id: values.region,
      account_country_id: values.country,
      account_zip_postal: values.postal_code,
    });
  };

  const validationSchema: Schema<OrganizationFormValues> = object().shape({
    org_name: string().required(formatMessage(messages.required)),
    same_as_billing_address: boolean().required(
      formatMessage(messages.required)
    ),
    address: string().when(
      'same_as_billing_address',
      ([sameAsBillingAddress], schema) =>
        sameAsBillingAddress
          ? schema.notRequired()
          : schema.required(formatMessage(messages.required))
    ),
    address2: string(),
    postal_code: string().when(
      'same_as_billing_address',
      ([sameAsBillingAddress], schema) =>
        sameAsBillingAddress
          ? schema.notRequired()
          : schema.required(formatMessage(messages.required))
    ),
    city: string().when(
      'same_as_billing_address',
      ([sameAsBillingAddress], schema) =>
        sameAsBillingAddress
          ? schema.notRequired()
          : schema.required(formatMessage(messages.required))
    ),
    region: string().when(
      'same_as_billing_address',
      ([sameAsBillingAddress], schema) =>
        sameAsBillingAddress
          ? schema.notRequired()
          : schema.required(formatMessage(messages.required))
    ),
    country: string().when(
      'same_as_billing_address',
      ([sameAsBillingAddress], schema) =>
        sameAsBillingAddress
          ? schema.notRequired()
          : schema.required(formatMessage(messages.required))
    ),
    email: emailSchema,
    phone_number: phoneSchema,
  });
  const gridItemProps: Grid2Props = {
    size: {
      xs: 12,
      md: 6,
    },
  };

  return (
    <ContentLoader lineCount={3} loading={loading}>
      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
      >
        {({ isSubmitting, setFieldValue, values }) => {
          const provinceOptions = getProvinceOptions(values.country);
          return (
            <Form>
              <Grid2 container columnSpacing={2}>
                <Grid2 size={{ xs: 12 }}>
                  <FormikTextField
                    label={formatMessage(messages.organizationName)}
                    name="org_name"
                    required
                  />
                </Grid2>
                <Grid2 size={{ xs: 12 }}>
                  <FormikToggleField
                    label={formatMessage(messages.sameBillingAddress)}
                    name="same_as_billing_address"
                    onChange={(value) =>
                      setFieldValue('same_as_billing_address', value)
                    }
                  />
                </Grid2>

                <Grid2 {...gridItemProps}>
                  <FormikDropdownField
                    label={formatMessage(messages.country)}
                    name="country"
                    onChange={(value) => setFieldValue('country', value)}
                    options={countryOptions}
                    required
                  />
                </Grid2>
                <Grid2 {...gridItemProps}>
                  <FormikDropdownField
                    label={formatMessage(messages.region)}
                    name="region"
                    onChange={(value) => setFieldValue('region', value)}
                    options={provinceOptions}
                    required
                  />
                </Grid2>

                <Grid2 {...gridItemProps}>
                  <FormikTextField
                    disabled={values.same_as_billing_address}
                    label={formatMessage(messages.address)}
                    name="address"
                  />
                </Grid2>
                <Grid2 {...gridItemProps}>
                  <FormikTextField
                    disabled={values.same_as_billing_address}
                    label={formatMessage(messages.address2)}
                    name="address2"
                  />
                </Grid2>
                <Grid2 {...gridItemProps}>
                  <FormikTextField
                    disabled={values.same_as_billing_address}
                    label={formatMessage(messages.city)}
                    name="city"
                  />
                </Grid2>
                <Grid2 {...gridItemProps}>
                  <FormikTextField
                    disabled={values.same_as_billing_address}
                    label={formatMessage(messages.postalCode)}
                    name="postal_code"
                  />
                </Grid2>

                <Grid2 {...gridItemProps}>
                  <FormikTextField
                    label={formatMessage(messages.email)}
                    name="email"
                  />
                </Grid2>
                <Grid2 {...gridItemProps}>
                  <FormikTelField
                    label={formatMessage(messages.phone)}
                    name="phone_number"
                  />
                </Grid2>

                <Grid2 size={{ xs: 12 }}>
                  <RoundedButton disabled={isSubmitting} type="submit">
                    {formatMessage(messages.submit)}
                  </RoundedButton>
                </Grid2>
              </Grid2>
            </Form>
          );
        }}
      </Formik>
    </ContentLoader>
  );
}
